import { render, html, nothing } from "lit";
import { dom, CustomProperties } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, CellSource, ColumnSource, RowSource, renderers } from "@omegagrid/grid";
import rp, { formatBytes, GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { ServerTreeAdapter } from "../adapters";
import { Accounting, Server } from "@reporting/rp-types/core";
import { RestoreDb2Info } from "@reporting/rp-types/server";
import { synchronizeServers } from "../utils/serverUtils";

type DataSource = {
	/** The text displayed in the cell */
	text: CellSource | string;
	/** The value of the cell */
	value?: Partial<CellSource>;
	/** The row controls*/
	controls?: Partial<CellSource>;
	/** The ID of the cell (hidden)*/
	id?: string;
}

type ExistenceCheckType = 'database' | 'user' | 'job' | 'table';

type ExistenceCheck = {
	/** POST param - what to check for */
    type?: ExistenceCheckType;
	/** POST param - the value to check; a table is checked by its full {db}.dbo.{table} name */
    value?: string;
	/** result from database */
    exists?: boolean;
};

type ValueProps = {
	/** placeholder text when no value is set */
	placeholder?: string;
	/** the object whose existence decides the icon in front of the value; no check, no icon */
	checkExists?: ExistenceCheck;
} & CustomProperties;

type RestoreDb2Layer = 'data' | 'master' | 'mdm';

/** what a create button builds; also the /create/{target} endpoint it posts to */
type CreateTarget = 'database' | 'master' | 'mdm' | 'mdmTable' | 'user' | 'job';

/** what the create endpoints answer with; objects that failed do not abort the whole build */
type CreateResult = {
	db_name?: string;
	error_count?: number;
	errors?: string;
};

type Btn = {
	/** button text */
	text: string;
	/** action to perform on click */
	action: 'create' | 'restore';
	/** icon name */
	icon: string;
	/** create target, or the layer of a restore */
	type: CreateTarget | RestoreDb2Layer;
};

export class DbSettingsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _rows: RowSource[];

	private accounting: Accounting;
	private accountingDefaults: Accounting;
	private server: Server;
	private exists: ExistenceCheck[] = [];

	constructor(
		private a: number,
	) { super() }

	async loadVariables() {
		rp.services.register('server');

		await this.ensureAccountingExists();
		this.accounting = await rp.ms('accounting').get<Accounting>(`${this.a}`, { accounting: this.a });
		if (!this.accounting) {
			rp.host.bucket.showAlert({ content: msg('Accounting not found.'), type: 'danger' });
			throw new Error(msg(`Accounting ${this.a} does not exist!`));
		}
		this.accountingDefaults = await rp.ms('accounting').get<Accounting>(`defaults`, { accounting: this.a });

		this.server = await rp.ms('server').get<Server>(`${this.accounting.id_server}`, { accounting: this.a });
		if (!this.server) {
			// the accounting points at a server this super database does not know; every request of
			// this accounting fails on the connection, so there is nothing sensible to render
			rp.host.bucket.showAlert({ content: `${msg('Server not found.')} (#${this.accounting.id_server})`, type: 'danger' });
			throw new Error(`Server ${this.accounting.id_server} does not exist!`);
		}

		// base databases, their _2 candidates and the currently set _2 (switch) values, deduplicated
		const dbNames = [
			this.accounting.super_db_name,
			this.accounting.data_db_name,
			this.accounting.master_db_name,
			this.accounting.mdm_db_name,
			this.db2Name('data'),
			this.db2Name('master'),
			this.db2Name('mdm'),
			this.db2Value('data'),
			this.db2Value('master'),
			this.db2Value('mdm'),
		].filter((v, i, arr) => v && arr.indexOf(v) === i);
		this.exists = await rp.ms('server').post<ExistenceCheck[]>(`exists`, [
			...dbNames.map(value => this.dbCheck(value)),
			this.userCheck(),
			this.jobCheck(),
			this.mdmTableCheck(),
		].filter(c => c.value), { accounting: this.a });
	}

	private dbCheck(value: string): ExistenceCheck {
		return { type: 'database', value };
	}

	private userCheck(): ExistenceCheck {
		return { type: 'user', value: this.accounting.etl_db_user };
	}

	private jobCheck(): ExistenceCheck {
		return { type: 'job', value: this.accounting.job_name };
	}

	/** the MDM table sits in the MDM database, not in the one this connection is on */
	private mdmTableCheck(): ExistenceCheck {
		const db = this.accounting.mdm_db_name, table = this.accounting.md_table;
		return { type: 'table', value: db && table ? `${db}.dbo.${table}` : undefined };
	}

	/** The source database of the given restore layer */
	private baseDbName(layer: RestoreDb2Layer): string {
		switch (layer) {
			case 'data': return this.accounting.data_db_name;
			case 'master': return this.accounting.master_db_name;
			case 'mdm': return this.accounting.mdm_db_name;
		}
	}

	/** The alternative database name the restore creates/refreshes */
	private db2Name(layer: RestoreDb2Layer): string {
		const base = this.baseDbName(layer);
		return base ? `${base}_2` : undefined;
	}

	/** The currently set _2 (switch) value of the given layer */
	private db2Value(layer: RestoreDb2Layer): string {
		switch (layer) {
			case 'data': return this.accounting.data_db_name_2;
			case 'master': return this.accounting.master_db_name_2;
			case 'mdm': return this.accounting.mdm_db_name_2;
		}
	}

	/**
	 * The database the restore button operates on: the stored _2 value when it names a restorable
	 * copy (keeps the _2 suffix), otherwise the {source}_2 convention. Mirrors getRestoreDb2Params
	 * on the backend.
	 */
	private restoreTarget(layer: RestoreDb2Layer): string {
		const stored = this.db2Value(layer);
		return stored && stored.endsWith('_2') && stored !== this.baseDbName(layer) ? stored : this.db2Name(layer);
	}

	private restoreBtn(layer: RestoreDb2Layer): Btn {
		// a cleared _2 value reads as "create" even when the copy still physically exists
		// (the confirmation dialog says overwrite in that case)
		const refresh = !!this.db2Value(layer) && this.checkIfExists(this.dbCheck(this.restoreTarget(layer)));
		return {
			text: `${refresh ? msg('Refresh database') : msg('Create database')} 2`,
			action: 'restore',
			icon: refresh ? 'rotate' : 'database',
			type: layer,
		};
	}

	private createBtn(type: CreateTarget, text: string, icon = 'database'): Btn {
		return { text, action: 'create', icon, type };
	}

	/**
	 * The accounting row is created on the server the admin is connected to and only reaches the
	 * accounting's own server through p_SYNC_SERVERS. Until then every request in this accounting's
	 * context (which connects by ID_SERVER) fails, so trigger the synchronization first.
	 * Only the two tables that context needs - the accounting row and the group every accounting
	 * query joins - each table being a full p_SYNC_TABLES round across the servers.
	 * Deliberately no accounting param - the check must run in the current, working context.
	 */
	private async ensureAccountingExists() {
		const exists = async () => (await rp.ms('accounting').get<{ exists: boolean }>(`/exists/${this.a}`))?.exists;
		if (await exists()) return;

		rp.host.bucket.showAlert({ content: `${msg('Accounting not found.')} ${msg('Server synchronization has been triggered.')} ${msg('Please wait...')}`, type: 'info', timeout: 8000 });
		await synchronizeServers(['P_ACCOUNTING', 'P_ACCOUNTING_GROUP']);
		if (await exists()) {
			rp.host.bucket.showAlert({ content: msg('Synchronization complete.'), type: 'success' });
		} else {
			// carrying on would only produce a confusing 500 from the connection layer
			rp.host.bucket.showAlert({ content: `${msg('Synchronization complete.')} ${msg('Accounting not found.')}`, type: 'danger' });
			throw new Error(`Accounting ${this.a} is not available on its server!`);
		}
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'text',
			t: 't',
			e: false,
			w: 180,
		}, {
			data: 'value',
			t: 't',
			s: { alignment: { horizontal: 'left' } },
			e: true,
			w: 300,
			renderer: this.valueRenderer, // default renderer for value cells
		}, {
			data: 'controls',
			t: 't',
			e: false,
			w: 160,
			renderer: this.controlRenderer,
		}, {
			data: 'id',
			t: 't',
			e: false,
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		await this.loadVariables();

		// the rows follow the order the objects have to be created in - master, MDM database and its
		// table, then the data database, the login and finally the job (see blockedBy)
		this._rows = [
			{ data: {
				text: msg('Server'),
				value: {
					t: 's',
					v: this.server.id_server.toString(),
					dv: this.server.host,
					adapter: new ServerTreeAdapter({ selectMode: 'single', autoClose: true, allowEmpty: false }),
					renderer: renderers.DropdownRenderer, // override default renderer to show dropdown
				},
				id: 'id_server',
			} as DataSource },
			{ data: {
				text: 'Super DB',
				value: {
					t: 't',
					v: this.accounting.super_db_name,
					props: { placeholder: this.accountingDefaults.super_db_name, checkExists: this.dbCheck(this.accounting.super_db_name) } as ValueProps
				},
				id: 'super_db_name',
			} as DataSource },
			{ data: {
				text: 'Master DB',
				value: {
					t: 't',
					v: this.accounting.master_db_name,
					props: { placeholder: this.accountingDefaults.master_db_name, checkExists: this.dbCheck(this.accounting.master_db_name) } as ValueProps
				},
				controls: {
					props: { btn: this.createBtn('master', msg('Create database')) }
				},
				id: 'master_db_name',
			} as DataSource },
			{ data: {
				text: 'Master DB 2',
				value: {
					t: 't',
					v: this.accounting.master_db_name_2,
					props: { checkExists: this.dbCheck(this.accounting.master_db_name_2) } as ValueProps
				},
				controls: {
					props: { btn: this.restoreBtn('master') }
				},
				id: 'master_db_name_2',
			} as DataSource },
			{ data: {
				text: 'MDM DB',
				value: {
					t: 't',
					v: this.accounting.mdm_db_name,
					props: { placeholder: this.accountingDefaults.mdm_db_name, checkExists: this.dbCheck(this.accounting.mdm_db_name) } as ValueProps
				},
				controls: {
					props: { btn: this.createBtn('mdm', msg('Create database')) }
				},
				id: 'mdm_db_name',
			} as DataSource },
			{ data: {
				text: 'MDM DB 2',
				value: {
					t: 't',
					v: this.accounting.mdm_db_name_2,
					props: { checkExists: this.dbCheck(this.accounting.mdm_db_name_2) } as ValueProps
				},
				controls: {
					props: { btn: this.restoreBtn('mdm') }
				},
				id: 'mdm_db_name_2',
			} as DataSource },
			{ data: {
				text: `MDM ${msg('table')}`,
				value: {
					t: 't',
					v: this.accounting.md_table,
					props: { placeholder: this.accountingDefaults.md_table, checkExists: this.mdmTableCheck() } as ValueProps
				},
				controls: {
					props: { btn: this.createBtn('mdmTable', msg('Create table'), 'table') }
				},
				id: 'md_table',
			} as DataSource },
			{ data: {
				text: 'Data DB',
				value: {
					t: 't',
					v: this.accounting.data_db_name,
					props: { placeholder: this.accountingDefaults.data_db_name, checkExists: this.dbCheck(this.accounting.data_db_name) } as ValueProps
				},
				controls: {
					props: { btn: this.createBtn('database', msg('Create database')) }
				},
				id: 'data_db_name',
			} as DataSource },
			{ data: {
				text: 'Data DB 2',
				value: {
					t: 't',
					v: this.accounting.data_db_name_2,
					props: { checkExists: this.dbCheck(this.accounting.data_db_name_2) } as ValueProps
				},
				controls: {
					props: { btn: this.restoreBtn('data') }
				},
				id: 'data_db_name_2',
			} as DataSource },
			{ data: {
				text: msg('User'),
				value: {
					t: 't',
					v: this.accounting.etl_db_user,
					props: { placeholder: this.accountingDefaults.etl_db_user, checkExists: this.userCheck() } as ValueProps
				},
				controls: {
					props: { btn: this.createBtn('user', msg('Create user'), 'user') }
				},
				id: 'etl_db_user',
			} as DataSource },
			{ data: {
				text: msg('Job'),
				value: {
					t: 't',
					v: this.accounting.job_name,
					props: { placeholder: this.accountingDefaults.job_name, checkExists: this.jobCheck() } as ValueProps
				},
				controls: {
					props: { btn: this.createBtn('job', msg('Create job'), 'briefcase') }
				},
				id: 'job_name',
			} as DataSource },
			{ data: {
				text: msg('Directory'),
				value: {
					t: 't',
					v: this.accounting.etl_dir,
					props: { placeholder: this.accountingDefaults.etl_dir } as ValueProps
				},
				id: 'etl_dir',
			} as DataSource },
			{ data: {
				text: msg('Cache enabled'),
				value: {
					t: 'b',
					v: this.accounting.cache,
					renderer: renderers.CheckboxRenderer // override default renderer to show checkbox
				},
				id: 'cache',
			} as DataSource }
		];
		return this._rows;
	}

	valueRenderer = (div: HTMLDivElement, cell: CellModel) => {
		const props = cell.properties as ValueProps;
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);
		const innerDiv = this.createInnerDiv();
		if (cell.value) {
			// only a real answer earns an icon - a row nobody checks (the ETL directory) shows none,
			// it used to show the green check of an object that had never been looked for
			render(cell.value ? html`
					${props?.edited || !props?.checkExists?.value ? '' :
						this.checkIfExists(props.checkExists)
						? html`<og-icon icon="check" style="color: green;"></og-icon>`
						: html`<og-icon icon="circle-exclamation" style="color: yellow;"></og-icon>`
					}
					<span>${cell.value}</span>
				` : ''
			, innerDiv);
		} else {
			render(props?.placeholder ? html`
					<og-icon icon="ghost" style="color: gray;"></og-icon>
					<span style="color: gray">${props.placeholder}</span>
				` : ''
			, innerDiv);
		}
		div.prepend(innerDiv);
		return div;
	}

	controlRenderer = (div: HTMLDivElement, cell: CellModel) => {
		const btn = cell.properties?.btn as Btn;
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);

		const innerDiv = this.createInnerDiv();
		render(btn ? html`
				<og-button
					icon="${btn.icon}"
					?disabled=${this.isDisabled(btn)}
					title=${this.alreadyExists(btn) ? nothing : this.blockedBy(btn) ?? nothing}
					color="blue"
					style="height: 18px; width: 100%;"
					text="${btn.text}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction(`${btn.action}.${btn.type}`, cell)}">
				</og-button>
			` : ''
		, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	/** a button is off once its object is there or while a prerequisite is missing */
	isDisabled(btn: Btn): boolean {
		return this.alreadyExists(btn) || !!this.blockedBy(btn);
	}

	/** the object a create button would build is already on the server */
	private alreadyExists(btn: Btn): boolean {
		if (btn.action === 'restore') return false;
		switch (btn.type as CreateTarget) {
			case 'database': return this.checkIfExists(this.dbCheck(this.accounting.data_db_name));
			case 'master': return this.checkIfExists(this.dbCheck(this.accounting.master_db_name));
			case 'mdm': return this.checkIfExists(this.dbCheck(this.accounting.mdm_db_name));
			case 'mdmTable': return this.checkIfExists(this.mdmTableCheck());
			case 'user': return this.checkIfExists(this.userCheck());
			case 'job': return this.checkIfExists(this.jobCheck());
			default: return false;
		}
	}

	/**
	 * What stands in the way of the button, shown as the tooltip of the disabled button; null when
	 * nothing does. Mirrors what the procedures need to get through, not what the application
	 * needs to run - the resulting order is master -> MDM -> MDM table -> data -> user -> job.
	 */
	private blockedBy(btn: Btn): string {
		const { master_db_name: master, mdm_db_name: mdm, md_table: mdmTable } = this.accounting;
		const masterDb = this.checkIfExists(this.dbCheck(master));
		const mdmDb = this.checkIfExists(this.dbCheck(mdm));
		const dataDb = this.checkIfExists(this.dbCheck(this.accounting.data_db_name));

		if (btn.action === 'restore') {
			return this.checkIfExists(this.dbCheck(this.baseDbName(btn.type as RestoreDb2Layer))) ? null : msg('The source database does not exist.');
		}
		switch (btn.type as CreateTarget) {
			case 'database':
				// p_CREATE_DATABASE bakes both names into the procedures it creates; a null name
				// nulls the whole script and leaves a database without a single procedure. The MDM
				// table is created through 'use <mdm db>', so that database has to exist
				if (!master || !mdm) return msg('Fill in the master and MDM database names first.');
				if (mdmTable && !mdmDb) return msg('Create the MDM database first.');
				return null;
			case 'mdmTable':
				return mdmDb ? null : msg('Create the MDM database first.');
			case 'user':
				// the login takes the data database as its default and is then mapped with 'use'
				// into every database the accounting names; a missing one fails the batch after the
				// login was created, and its password is lost with it
				if (!dataDb) return msg('Create the data database first.');
				if (master && !masterDb) return msg('Create the master database first.');
				if (mdm && !mdmDb) return msg('Create the MDM database first.');
				return null;
			case 'job':
				if (!dataDb) return msg('Create the data database first.');
				if (!this.checkIfExists(this.userCheck())) return msg('Create the user first.');
				return null;
			default:
				return null;
		}
	}

	/**
	 * Matched on the type as well: a database and a login of the same name are the rule here, not
	 * the exception (Reporting_G5 is both), so a value alone answers the wrong question.
	 */
	checkIfExists(check: ExistenceCheck): boolean {
		if (!check?.value) return false;
		return this.exists.some(e => e.type === check.type && e.value === check.value && e.exists);
	}

	createInnerDiv(): HTMLDivElement {
		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '4px';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.minHeight = '22px';
		return innerDiv;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: false,
			filters: false,
			refreshAfterSave: true,
			rowTree: false,
			saveMode: 'edited_rows',
			toolbar: {
				new: { enabled: false },
			},
		});
	}

	async save(rowData: RowData[]) {
		const data: Accounting = { accounting: this.a };
		for (const d of rowData.map(row => (row.data as DataSource))) {
			if (d.id) (data as any)[d.id] = d.value;
		}
		if (data.id_server != null && Number(data.id_server) !== this.accounting.id_server) {
			const command = await rp.host.openMenu({
				content: 'yes_no',
				text: `${msg('Do you want to move this accounting to another server?')} ${msg('The databases are not moved with it.')}`
			});
			if (command?.id != 'Y') return Promise.reject('user cancelled');
		}
		await rp.ms('accounting').post('accountings', data, { accounting: this.a });
	}

	async onAction(action: string, cell: CellModel) {
		const [act, type] = action.split('.');
		if (!type) return;
		if (act === 'restore') {
			await this.restoreDb2(type as RestoreDb2Layer);
			return;
		}
		if (act === 'create') {
			await this.createDb(type as CreateTarget);
			return;
		}
	}

	/**
	 * An empty name is filled in from the defaults and saved first - the procedures read the value
	 * from P_ACCOUNTING, not from the request. Master and MDM databases belong to a whole group,
	 * so the confirmation says which object is about to appear on the server.
	 */
	private async createDb(type: CreateTarget) {
		if (!this.editor.isSaved) {
			rp.host.bucket.showAlert({ content: msg('Save your changes first!'), type: 'warning', title: msg('Database') });
			return;
		}

		const accounting2: Accounting = { accounting: this.a };
		let val: string;
		switch (type) {
			case 'database':
				val = this.accounting.data_db_name || (accounting2.data_db_name = this.accountingDefaults.data_db_name);
				break;
			case 'master':
				val = this.accounting.master_db_name || (accounting2.master_db_name = this.accountingDefaults.master_db_name);
				break;
			case 'mdm':
				val = this.accounting.mdm_db_name || (accounting2.mdm_db_name = this.accountingDefaults.mdm_db_name);
				break;
			case 'mdmTable':
				val = this.accounting.md_table || (accounting2.md_table = this.accountingDefaults.md_table);
				break;
			case 'user':
				val = this.accounting.etl_db_user || (accounting2.etl_db_user = this.accountingDefaults.etl_db_user);
				break;
			case 'job':
				val = this.accounting.job_name || (accounting2.job_name = this.accountingDefaults.job_name);
				break;
			default: return;
		}
		if (!val) {
			rp.host.bucket.showAlert({ content: msg('Fill in the name first!'), type: 'warning', title: msg('Database') });
			return;
		}

		const command = await rp.host.openMenu({ content: 'yes_no', text: `${msg('Do you want to create')} "${val}"?` });
		if (command?.id == 'Y') {
			this.editor.showLoader();
			try {
				if (Object.keys(accounting2).length > 1) {
					await rp.ms('accounting').post('accountings', accounting2, { accounting: this.a });
				}
				const result = await rp.ms('server').post<CreateResult>(`/create/${type}`, {}, { accounting: this.a });
				// the procedures do not abort on a single object they cannot build; what is missing
				// is worth saying out loud, the rest of the database is there
				if (result?.error_count) {
					rp.host.bucket.showAlert({ content: `${msg('Created successfully')} "${val}". ${msg('Some objects failed:')} ${result.errors}`, type: 'warning', timeout: 15000, title: msg('Database') });
				} else {
					rp.host.bucket.showAlert({ content: `${msg('Created successfully')} "${val}".`, type: 'success', title: msg('Database') });
				}
			} catch (e) {
				console.error(e.data?.error ?? e);
				rp.host.bucket.showAlert({ content: `${msg('Failed to create')} "${val}": ${e.data?.error ?? e}`, type: 'danger', title: msg('Database') });
			} finally {
				this.editor.refresh();
			}
		}
	}

	/**
	 * Backup + restore of the layer's database into its `_2` copy, run on the server as a background
	 * SQL Agent job. The confirmation shows what will be created/overwritten, the source size and how
	 * many accountings share the source DB;
	 */
	private async restoreDb2(layer: RestoreDb2Layer) {
		if (!this.editor.isSaved) {
			rp.host.bucket.showAlert({ content: msg('Save your changes first!'), type: 'warning', title: msg('Database') });
			return;
		}

		let info: RestoreDb2Info;
		try {
			info = await rp.ms('server').get<RestoreDb2Info>(`restore/db2/${layer}`, { accounting: this.a });
		} catch (e) {
			console.error(e.data?.error ?? e);
			return;
		}

		if (info.running) {
			rp.host.bucket.showAlert({ content: `${msg('Restore is already running.')} (${info.db_name_2})`, type: 'warning', title: msg('Database') });
			return;
		}

		const size = formatBytes(info.size);
		const parts = [
			info.db2_exists
				? `${msg('Do you want to overwrite')} "${info.db_name_2}" ${msg('with a copy of')} "${info.db_name}" (${size})?`
				: `${msg('Do you want to create')} "${info.db_name_2}" ${msg('as a copy of')} "${info.db_name}" (${size})?`
		];
		if (info.accountings > 1) parts.push(`${msg('The source database is used by {a} accountings.', { data: { a: info.accountings } })}`);
		if (info.db2_exists) {
			parts.push(msg('Users connected to DB2 will be disconnected.'));
		} else {
			parts.push(`${msg('Once the restore succeeds, DB2 will be set to "{db2}".', { data: { db2: info.db_name_2 } })}`);
		}

		const command = await rp.host.openMenu({ content: 'yes_no', text: parts.join(' ') });
		if (command?.id != 'Y') return;
		try {
			const result = await rp.ms('server').post<{ job: string }>(`restore/db2/${layer}`, {}, { accounting: this.a });
			rp.host.bucket.showAlert({ content: `${msg('Restore started as job "{job}".', { data: { job: result.job } })} ${msg('See journal for more details.')}`, type: 'success', timeout: 8000, title: msg('Database') });
		} catch (e) {
			console.error(e.data?.error ?? e);
		}
	}

}
